import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import classnames from 'classnames';

import {
  COMMON_FORM_FIELD_CLASSES,
  FORM_FIELD_VARIANT_TYPES
} from 'dpl/shared/form/utils/constants';

import ErrorWrapper from './ErrorWrapper';
import Label from './Label';

type VariantType =
  (typeof FORM_FIELD_VARIANT_TYPES)[keyof typeof FORM_FIELD_VARIANT_TYPES];

export const VARIANT_CLASSES: Record<VariantType, string> = {
  [FORM_FIELD_VARIANT_TYPES.LARGE]: `${COMMON_FORM_FIELD_CLASSES} pv4 ph4 pv5-md fw-medium`,
  [FORM_FIELD_VARIANT_TYPES.REGULAR]: `${COMMON_FORM_FIELD_CLASSES} pv2 ph2`
};

function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
  // Does not allow non-numeric values for number input
  if (/[^0-9-.]/.test(event.key)) {
    event.preventDefault();
  }
}

function handleOnWheel(event: React.WheelEvent<HTMLInputElement>) {
  // Prevent value change on scroll for number inputs
  event.currentTarget.blur();
}

/** Native input props we pass through via `...inputProps` (includes `type`, etc.). */
export interface SimpleInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    | 'id'
    | 'name'
    | 'onChange'
    | 'value'
    | 'autoComplete'
    | 'className'
    | 'required'
  > {
  autoComplete?: string | null;
  className?: string | null;
  displayErrors?: boolean;
  errors?: React.ReactNode[];
  id?: string | null;
  label?: string | null;
  name: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  value?: string | number | null;
  newDesignSystemStyles?: boolean;
  variant?: VariantType;
  /** When true, cursor will always move to end of input on value change */
  moveCursorToEnd?: boolean;
}

const SimpleInput = forwardRef<HTMLInputElement, SimpleInputProps>(
  (
    {
      autoComplete = null,
      className = null,
      displayErrors = false,
      errors = [],
      id = null,
      label = null,
      name,
      onChange,
      required = false,
      value = null,
      variant = FORM_FIELD_VARIANT_TYPES.REGULAR,
      newDesignSystemStyles = false,
      moveCursorToEnd = false,
      ...inputProps
    },
    ref
  ) => {
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
      if (!inputRef.current) {
        return;
      }

      try {
        const targetPosition = moveCursorToEnd
          ? (value ?? '').toString().length
          : cursorPosition;

        if (targetPosition !== null) {
          inputRef.current.setSelectionRange(targetPosition, targetPosition);
        }
      } catch (_err) {
        // ! Moving the cursor has been purposefully removed from some input
        // ! types (e.g. email, number). To prevent future revocations from
        // ! breaking the input catch all related errors.
      }
    }, [ref, cursorPosition, value, moveCursorToEnd]);

    return (
      <div className="Input">
        {label && (
          <Label
            htmlFor={id}
            text={label}
            newDesignSystemStyles={newDesignSystemStyles}
          />
        )}
        <ErrorWrapper isShown={displayErrors} errors={errors}>
          <input
            // autoComplete "off" does not prevent the user from autofilling but "nope" does
            autoComplete={
              autoComplete === 'off' ? 'nope' : (autoComplete ?? undefined)
            }
            className={classnames(
              'Input__input',
              VARIANT_CLASSES[variant],
              {
                'pv3 ph4 font-16': newDesignSystemStyles
              },
              className
            )}
            data-test-id="form-input"
            id={id ?? undefined}
            name={name}
            required={required}
            onChange={event => {
              onChange(event);
              setCursorPosition(event.target.selectionStart ?? null);
            }}
            onKeyPress={
              inputProps.type === 'number' ? handleKeyPress : undefined
            }
            onWheel={inputProps.type === 'number' ? handleOnWheel : undefined}
            value={value ?? ''}
            {...inputProps}
            ref={inputRef}
          />
        </ErrorWrapper>
      </div>
    );
  }
);

SimpleInput.displayName = 'SimpleInput';

export default SimpleInput;
